import apiClient from "./apiService";
// import debugService from "./debugService";
// import userAuthService from "./userAuthService";

export interface FeedbackRequest {
  name: string;
  email: string;
  feedback: {
    product_name?: string;
    type: "feedback" | "question";
    text: string;
    rating?: number;
    product_id?: string;
    product_link?: string;
    photos?: string[];
    videos?: string[];
    pros?: string;
    cons?: string;
    optional_fields?: Record<string, string | number | boolean>;
  };
}

export interface FeedbackResponse {
  id: number;
  feedback_id: string;
  type: string;
  product_name: string;
  date: string;
  text: string;
  rating: number;
  photos: string[];
  videos: string[];
  customer_name: string;
  optional_fields: Record<string, string | number | boolean>;
  marketplace_type: string;
  source: string;
  pros: string;
  cons: string;
}

export const feedbackService = {
  /**
   * Отправляет отзыв о продукте
   * @param reviewData Данные отзыва
   * @returns Promise с результатом отправки
   */
  submitReview: async (
    reviewData: Omit<FeedbackRequest["feedback"], "type"> & {
      name: string;
      email: string;
    }
  ): Promise<FeedbackResponse> => {
    try {
      const { name, email, ...feedbackData } = reviewData;

      const formattedPhotos = feedbackData.photos
        ? feedbackData.photos.map((url) => ({ url }))
        : [];

      const formattedVideos = feedbackData.videos
        ? feedbackData.videos.map((url) => ({ url }))
        : [];
      const response = await apiClient.post<FeedbackResponse>("widget/submit", {
        name,
        email,
        feedback: {
          type: "feedback",
          text: feedbackData.text,
          rating: feedbackData.rating,
          product_id: feedbackData.product_id,
          product_name: feedbackData.product_name,
          product_link: feedbackData.product_link || "",
          photos: formattedPhotos,
          videos: formattedVideos,
          pros: feedbackData.pros || "",
          cons: feedbackData.cons || "",
          optional_fields: feedbackData.optional_fields,
        },
      });

      return response.data;
    } catch (error) {
      console.log("Error submitting review: ", error);
      throw error;
    }
  },

  /**
   * Отправляет вопрос о продукте
   * @param questionData Данные вопроса
   * @returns Promise с результатом отправки
   */
  submitQuestion: async (
    questionData: Omit<
      FeedbackRequest["feedback"],
      "type" | "rating" | "photos" | "videos" | "pros" | "cons"
    > & { name: string; email: string }
  ): Promise<FeedbackResponse> => {
    try {
      const { name, email, ...feedbackData } = questionData;

      // Для вопросов используем тот же эндпоинт, но с типом "question"
      const response = await apiClient.post<FeedbackResponse>("widget/submit", {
        name,
        email,
        feedback: {
          ...feedbackData,
          type: "question",
          rating: 0,
          photos: [],
          videos: [],
          pros: "",
          cons: "",
          product_link: "",
        },
      });

      return response.data;
    } catch (error) {
      console.log("Error submitting question: ", error);
      throw error;
    }
  },

  /**
   * Загружает фото и видео файлы для отзыва или вопроса
   * @param files Массив файлов для загрузки
   * @returns Promise с массивом URL файлов
   */
  uploadFiles: async (files: File[]): Promise<string[]> => {
    if (!files.length) return [];

    try {
      const formData = new FormData();

      // API documentation clearly specifies 'files' as the parameter name
      for (const file of files) {
        formData.append("files", file);
      }

      // Send request to the upload-media endpoint
      const response = await apiClient.post<{ urls: string[] }>(
        "/widget/upload-media",
        formData,
        {
          headers: {
            // Override the default Content-Type to let axios set the correct multipart boundary
            "Content-Type": undefined,
          },
        }
      );

      console.log("uploadFiles response: ", response.data);
      return response.data.urls;
    } catch (error) {
      console.log("Error uploading media files: ", error);
      throw error;
    }
  },
};

export default feedbackService;
